import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setActiveUnitError,
  setAdditionalActiveUnitError,
} from 'store/units/reducer';
import type { UnitInfoFeedInterface } from 'store/units/types';
import type { RootState } from 'store';
import * as S from '../../PropertyPanel.styles';
import * as FeedConfig from 'unitData/Feed/FeedData';
import { PsdTable } from './PsdTable';
import { PSDCurve } from './PsdCurve';
import { NumberInput } from 'components/NumberInput';
import { RadioButtonNumberInput } from 'components/RadioButtonNumberInput';
import { Dropdown } from 'components/Dropdown';
import { TopCard } from 'components/TopCard';
import { StringInput } from 'components/StringInput';
import { SideCard } from 'components/SideCard';
import { InfoBar } from 'components/InfoBar';
import { FlowProperties } from 'containers/FlowProperties';
import {
  CONVERSIONS,
  convertToCurrentMetric,
  getMetricUnit,
} from 'util/conversions';

import { feedTypes } from 'unitData/Feed/FeedData';
import type { FeedUnitInfoDataInterface } from 'unitData/Feed/unitInfoData';
import { useEditUnitInfo } from 'containers/PropertyPanel/util/hooks/useEditUnitInfo';
import type { UnitCardProps } from 'containers/PropertyPanel/types';
import DisplayCard from 'containers/PropertyPanel/components/DisplayCard';
import { CheckboxWithLabel } from 'components/CheckboxWithLabel';
import { useEditInputFlow } from 'containers/PropertyPanel/util/hooks/useEditInputFlow';
import { usePrevious } from 'hooks/usePrevious';
import { useMetricType } from 'hooks/useMetricType';
import PropertyPanelContent from 'containers/PropertyPanel/components/PropertyPanelContent';
import PropertyPanelAccordion from 'containers/PropertyPanel/components/PropertyPanelAccordion';
import { Stack } from '@mui/material';
import { findOption, toDisplayOption } from 'unitData/options/optionResolution';

enum CUSTOM_MATERIAL {
  CUSTOM = 'Custom',
}

interface updatePsdInterface {
  name: string;
  percent: number[];
  size: number[];
  maxStoneSize: number;
}

export const convertBulkDensity = (specific: number) => {
  return (specific / 2.65) * 1.6;
};

export const FeedCard = ({
  id,
  unitInfo,
}: UnitCardProps<UnitInfoFeedInterface>) => {
  const dispatch = useDispatch();

  const metricType = useMetricType();
  const { activeUnitError, additionalActiveUnitError, showFlowProperties } =
    useSelector((state: RootState) => state.unitConnGroup.present.unitState);
  const unitInfoData: FeedUnitInfoDataInterface = useSelector(
    (state: RootState) => state.unitInfoDataState.unitInfoData.FEED,
  );

  const { setValue } = useEditUnitInfo<UnitInfoFeedInterface>(id, unitInfo);
  const { setValue: setFlowValue, setValues: setFlowValues } = useEditInputFlow(
    id,
    unitInfo.inputFlow,
  );

  const {
    radioMode,
    feedImg: feedImageId,
    display: { displayPsdList, displayPsdGraph },
  } = unitInfo;

  const {
    materialName,
    materialId,
    otherMaterialName,
    bulkDensity,
    specificGravity,
    workIndex,
    abrasionIndex,
    moisture,
    clay,
    totalMass,
    psdName,
    psdId,
    otherPsdName,
    maxStoneSize,
    psd: percent,
    size,
  } = unitInfo.inputFlow;

  const selectedMaterialId = materialId ?? materialName;
  const selectedPsdId = psdId ?? psdName;
  const selectedPsd = findOption(unitInfoData.psdCurves, selectedPsdId);
  const selectedPsdName = selectedPsd?.name ?? psdName;
  const prevMaterialId = usePrevious(selectedMaterialId);

  const [currentMetricType, setCurrentMetricType] = useState(metricType);

  const [curveEditIndex, setCurveEditIndex] = useState<number | undefined>(
    undefined,
  );

  const feedImage = feedTypes.find((t) => t.id === feedImageId);

  const [showCardColumn, setShowCardColumn] = useState(false);

  const convertSpecificDensity = (bulk: number) => {
    return (bulk * 2.65) / 1.6;
  };

  /* Handle new values if pre-defined material changes */
  useEffect(() => {
    if (prevMaterialId === undefined || prevMaterialId === selectedMaterialId) {
      return;
    }
    const materialData = findOption(
      unitInfoData.predefinedMaterials,
      selectedMaterialId,
    );

    if (materialData) {
      setFlowValues({
        specificGravity: materialData.specificGravity,
        bulkDensity: materialData.bulkDensity,
        workIndex: materialData.workIndex,
        abrasionIndex: materialData.abrasionIndex,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterialId, unitInfoData.predefinedMaterials]);

  useEffect(() => {
    if (currentMetricType !== metricType) {
      setCurrentMetricType(metricType);
    }
  }, [metricType, currentMetricType]);

  useEffect(() => {
    if (
      typeof moisture !== 'number' ||
      typeof bulkDensity !== 'number' ||
      typeof specificGravity !== 'number' ||
      typeof clay !== 'number' ||
      typeof workIndex !== 'number' ||
      typeof abrasionIndex !== 'number' ||
      typeof totalMass !== 'number'
    ) {
      dispatch(setActiveUnitError(true));
    }
    const checkPsd = percent.find((p) => p === 100) !== undefined;
    dispatch(setAdditionalActiveUnitError(!checkPsd));
  }, [
    abrasionIndex,
    bulkDensity,
    clay,
    dispatch,
    moisture,
    percent,
    specificGravity,
    totalMass,
    workIndex,
  ]);

  // assumption is only called if something has been updated from the base values!
  const updatePsd = ({
    name: newPsdName,
    percent: newPsd,
    size: newSize,
    maxStoneSize: newMaxStoneSize,
  }: updatePsdInterface) => {
    let newOtherPsdName = otherPsdName;
    let psd = psdName;

    if (newPsdName !== CUSTOM_MATERIAL.CUSTOM) {
      newOtherPsdName = `${CUSTOM_MATERIAL.CUSTOM} - ${psdName}`;
      psd = CUSTOM_MATERIAL.CUSTOM;
    }

    setFlowValues({
      psd: newPsd,
      size: newSize,
      maxStoneSize: newMaxStoneSize,
      otherPsdName: newOtherPsdName,
      psdId: undefined,
      psdName: psd,
    });
  };

  const changePsd = (selected: string) => {
    const selectedRelevantPart = selected.split(' (')[0];

    if (selectedRelevantPart === CUSTOM_MATERIAL.CUSTOM) {
      setFlowValues({
        psd: [0],
        size: [0],
        psdId: undefined,
        psdName: selectedRelevantPart,
        otherPsdName: undefined,
        maxStoneSize: undefined,
      });
      return;
    }

    const psdData = findOption(unitInfoData.psdCurves, selectedRelevantPart);
    if (psdData?.psd && psdData?.size) {
      setFlowValues({
        psd: psdData.psd,
        size: psdData.size,
        psdId: psdData.id,
        psdName: psdData.name,
        otherPsdName: undefined,
        maxStoneSize: psdData.maxStoneSize,
      });
    }
  };

  if (showFlowProperties) {
    return <FlowProperties id={id} />;
  }

  const onSelectMaterial = (val: string) => {
    if (val === CUSTOM_MATERIAL.CUSTOM) {
      setFlowValues({
        otherMaterialName: `${CUSTOM_MATERIAL.CUSTOM} - ${materialName}`,
        materialId: undefined,
        materialName: val,
      });
    } else {
      const material = findOption(unitInfoData.predefinedMaterials, val);
      if (!material) return;
      setFlowValues({
        otherMaterialName: undefined,
        materialId: material.id,
        materialName: material.name,
      });
    }
  };

  return (
    <S.MultipleCardColumnsContainer>
      <PropertyPanelContent
        title='Feed'
        showActionBar={true}
        displayCard={
          <DisplayCard
            extra={
              <>
                <CheckboxWithLabel
                  value={displayPsdList}
                  setValue={(val) =>
                    setValue(['display', 'displayPsdList'], val)
                  }
                  label={`Particle size distribution list`}
                />
                <CheckboxWithLabel
                  value={displayPsdGraph}
                  setValue={(val) =>
                    setValue(['display', 'displayPsdGraph'], val)
                  }
                  label={`Particle size distribution graph`}
                />
              </>
            }
          />
        }
      >
        <PropertyPanelAccordion title='Material Properties' defaultExpanded>
          <TopCard>
            {feedImage && (
              <Stack
                sx={{
                  alignItems: 'center',
                }}
              >
                <S.CustomImage
                  key={id}
                  alt={'feed-img'}
                  src={feedImage?.image2}
                />
              </Stack>
            )}
            <Dropdown
              label={'Feed type'}
              value={String(feedImage?.id ?? '')}
              setValue={(id) => {
                const feedType = feedTypes.find((t) => String(t.id) === id);
                if (feedType) {
                  setValue('feedImg', feedType.id);
                }
              }}
              dropDownOptions={feedTypes.map((elem) => ({
                value: String(elem.id),
                label: elem.displayName,
              }))}
            />
          </TopCard>
          <Dropdown
            value={
              materialName !== CUSTOM_MATERIAL.CUSTOM
                ? selectedMaterialId
                : CUSTOM_MATERIAL.CUSTOM
            }
            setValue={(val) => onSelectMaterial(val)}
            label={`Predefined material`}
            dropDownOptions={unitInfoData.predefinedMaterials.map(
              toDisplayOption,
            )}
            customDropDownOption={CUSTOM_MATERIAL.CUSTOM}
          />
          {materialName === CUSTOM_MATERIAL.CUSTOM && (
            <StringInput
              value={otherMaterialName}
              setValue={(val) =>
                setFlowValue(
                  'otherMaterialName',
                  val?.length > 0 ? val : undefined,
                )
              }
              label={`Material name`}
            />
          )}
          <RadioButtonNumberInput
            value={specificGravity}
            conversionType={CONVERSIONS.DENSITY}
            setValue={(specific) => {
              let bulk = 0;
              if (specific) {
                bulk = convertBulkDensity(specific);
                setFlowValues({
                  specificGravity: specific,
                  bulkDensity: parseFloat(bulk.toFixed(2)),
                });
              }
            }}
            setActiveRadio={() =>
              setValue('radioMode', FeedConfig.RADIO_MODE.GRAVITY)
            }
            checked={radioMode === FeedConfig.RADIO_MODE.GRAVITY}
            label={`Specific gravity`}
            metric={getMetricUnit(metricType, CONVERSIONS.DENSITY)}
            step={'0.1'}
          />
          <RadioButtonNumberInput
            value={bulkDensity}
            conversionType={CONVERSIONS.DENSITY}
            setValue={(bulk) => {
              let specific = 0;
              if (bulk) {
                specific = convertSpecificDensity(bulk);
                setFlowValues({
                  bulkDensity: bulk,
                  specificGravity: parseFloat(specific.toFixed(2)),
                });
              }
            }}
            setActiveRadio={() =>
              setValue('radioMode', FeedConfig.RADIO_MODE.DENSITY)
            }
            checked={radioMode === FeedConfig.RADIO_MODE.DENSITY}
            label={`Bulk density`}
            metric={getMetricUnit(metricType, CONVERSIONS.DENSITY)}
            step={'0.1'}
          />
          <S.FormContainer>
            <NumberInput
              value={moisture}
              setValue={(val: number) => {
                setFlowValue('moisture', val);
                setValue('moistureUI', val);
              }}
              label={`Moisture [% by weight]`}
              marginRight={16}
            />
            <NumberInput
              value={clay}
              setValue={(val: number) => setFlowValue('clay', val)}
              label={`Clay [% by weight]`}
            />
          </S.FormContainer>
          <S.FormContainer>
            <NumberInput
              value={workIndex}
              setValue={(val: number) => setFlowValue('workIndex', val)}
              label={`Work index`}
              marginRight={16}
            />
            <NumberInput
              value={FeedConfig.calcCompensatedWorkIndex(
                workIndex,
                specificGravity,
              )}
              setValue={(_val: number) => {}}
              label={`Compensated W.I.`}
              disabled
              optional
            />
          </S.FormContainer>
          <S.FormContainer>
            <NumberInput
              value={abrasionIndex}
              setValue={(val: number) => setFlowValue('abrasionIndex', val)}
              label={`Abrasion index`}
              marginRight={16}
              step={'0.1'}
            />
            <NumberInput
              value={totalMass}
              conversionType={CONVERSIONS.CAPACITY}
              setValue={(val: number) => setFlowValue('totalMass', val)}
              label={`Feed rate [${getMetricUnit(
                metricType,
                CONVERSIONS.CAPACITY,
              )}]`}
            />
          </S.FormContainer>
          {activeUnitError && (
            <InfoBar type='information-warning' title={'Undefined value'} />
          )}
        </PropertyPanelAccordion>
        <PropertyPanelAccordion title='Particle size distribution'>
          <PsdTable
            psdName={
              psdName !== CUSTOM_MATERIAL.CUSTOM && maxStoneSize !== undefined
                ? `${selectedPsdName} (<${convertToCurrentMetric({
                    value: maxStoneSize,
                    metricType,
                    conversionType: CONVERSIONS.MILLIMETER,
                  })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)})`
                : CUSTOM_MATERIAL.CUSTOM
            }
            psdValue={selectedPsdId}
            otherPsdName={otherPsdName}
            setOtherPsdName={(val) => setFlowValue('otherPsdName', val)}
            enableFocusedCell={true}
            percent={percent}
            showCardColumn={showCardColumn}
            maxStoneSize={maxStoneSize}
            size={size}
            updatePsd={updatePsd}
            curveEditIndex={curveEditIndex}
            materialOptions={unitInfoData.psdCurves.map((dist) => ({
              value: dist.id,
              label: `${dist.name} (<${convertToCurrentMetric({
                value: dist.maxStoneSize,
                metricType,
                conversionType: CONVERSIONS.MILLIMETER,
              })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)})`,
            }))}
            hidePsd={false}
            toggleShowCardColumn={() => {
              setShowCardColumn(!showCardColumn);
            }}
            changePsd={changePsd}
            conversionType={CONVERSIONS.MILLIMETER}
          />
          {additionalActiveUnitError && (
            <InfoBar
              type='information-warning'
              title={
                'Incomplete PSD curve. Please add size where 100 % is passing.'
              }
            />
          )}
        </PropertyPanelAccordion>
      </PropertyPanelContent>
      <SideCard
        expandValue={showCardColumn}
        title={`Flow curve`}
        closeCard={() => {
          setShowCardColumn(false);
        }}
      >
        <PSDCurve
          percent={percent}
          size={size}
          updatePsd={(newPsd: number[], newSize: number[]) => {
            setFlowValues({
              psd: newPsd,
              size: newSize,
              otherPsdName: `${CUSTOM_MATERIAL.CUSTOM} - ${selectedPsdName}`,
              psdId: undefined,
              psdName: CUSTOM_MATERIAL.CUSTOM,
            });
          }}
          setCurveEditIndex={(editIndex: number | undefined) =>
            setCurveEditIndex(editIndex)
          }
        />
      </SideCard>
    </S.MultipleCardColumnsContainer>
  );
};
